import type { CalEvent, Holiday } from "../../api/types";
import { HOLIDAY_COLOR } from "../../lib/colors";
import { MONTHS_SHORT, WEEKDAYS_SHORT, datePart, formatTime, minutesOf, parts, weekday, type DateStr } from "../../lib/date";
import { anchorOf, useUi } from "../../state/ui";
import { useHolidayPopover } from "./common";
import "./EventList.css";

type Row = { day: DateStr; event?: CalEvent; holiday?: Holiday };

/** Agenda-style list grouped by day (Programação view and search results). */
export function EventList({ events, holidays = [], emptyText, showYear }: { events: CalEvent[]; holidays?: Holiday[]; emptyText: string; showYear?: boolean }) {
  const ui = useUi();
  const holidayPopover = useHolidayPopover();
  const rows: Row[] = [
    ...holidays.map((h): Row => ({ day: h.date, holiday: h })),
    ...events.map((e): Row => ({ day: datePart(e.start), event: e }))
  ].sort((a, b) => a.day.localeCompare(b.day) || Number(Boolean(b.holiday)) - Number(Boolean(a.holiday)) || (a.event?.start ?? "").localeCompare(b.event?.start ?? ""));

  if (!rows.length) return <div className="el-empty">{emptyText}</div>;

  let lastDay = "";
  return (
    <div className="el">
      {rows.map((r, i) => {
        const first = r.day !== lastDay;
        lastDay = r.day;
        const { y, m, d } = parts(r.day);
        const e = r.event;
        const color = e?.color ?? HOLIDAY_COLOR;
        const when = !e ? "Dia inteiro" : e.allDay ? "Dia inteiro" : `${formatTime(minutesOf(e.start))} – ${formatTime(minutesOf(e.end))}`;
        return (
          <div key={`${r.day}:${e?.key ?? r.holiday?.name}:${i}`} className={`el-row${first ? " first" : ""}`}>
            <button className={`el-date${r.day === ui.today ? " today" : ""}`} onClick={() => { ui.setDate(r.day); ui.setView("day"); }} style={{ visibility: first ? "visible" : "hidden" }}>
              <span className="el-daynum">{d}</span>
              <span className="el-mon">
                {MONTHS_SHORT[m - 1].toUpperCase()}
                {showYear ? ` ${y}` : ""}, {WEEKDAYS_SHORT[weekday(r.day)].toUpperCase()}
              </span>
            </button>
            <button
              className="el-item"
              onClick={(ev) => {
                if (e) ui.setOverlay({ kind: "details", event: e, anchor: anchorOf(ev.currentTarget) });
                else if (r.holiday) holidayPopover.open(r.holiday, anchorOf(ev.currentTarget));
              }}
            >
              <span className="color-dot" style={{ background: color }} />
              <span className="el-time">{when}</span>
              <span className="el-title">
                {e ? e.title || "(sem título)" : r.holiday?.name}
                {e?.location && <span className="el-loc"> · {e.location}</span>}
              </span>
            </button>
          </div>
        );
      })}
      {holidayPopover.element}
    </div>
  );
}
